/**
 * ============================================================
 * KEJJA OS — src/lib/routinesApi.ts
 * ============================================================
 */

import { api } from '@/lib/api';

export type RoutineCadence = 'daily' | 'weekly' | 'custom_weekdays' | 'monthly';

export interface RoutineSchedule {
  dayOfWeek?: number;
  weekdays?: number[];
  day?: number;
}

export interface Routine {
  id: string;
  homeId: string;
  userId: string | null;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  cadence: RoutineCadence;
  scheduleJson: RoutineSchedule;
  timeOfDay: string | null;
  isActive: boolean;
  sortOrder: number;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface TodayRoutineView {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  timeOfDay: string | null;
  cadence: RoutineCadence;
  userId: string | null;
  isDueToday: boolean;
  completedToday: boolean;
  completedByUserId: string | null;
  completedAt: string | null;
  streak: number;
}

export interface RoutineCheckin {
  id: string;
  homeId: string;
  routineId: string;
  userId: string;
  completedForDate: string;
  completedAt: string;
  note: string | null;
  createdAt: string;
}

export interface RoutinesPulse {
  today: string;
  totalActive: number;
  dueToday: number;
  completedToday: number;
  longestStreak: number;
  topPending: Array<{
    id: string; name: string; userId: string | null;
    icon: string | null; color: string | null; streak: number;
  }>;
}

const base = (homeId: string) => `/homes/${homeId}/routines`;

export const routinesApi = {
  pulse: (homeId: string) =>
    api.get<RoutinesPulse>(`${base(homeId)}/pulse`),

  today: (homeId: string) =>
    api.get<{ routines: TodayRoutineView[] }>(`${base(homeId)}/today`),

  list: (homeId: string) =>
    api.get<{ routines: Routine[] }>(base(homeId)),

  create: (homeId: string, body: {
    name: string;
    description?: string | null;
    userId?: string | null;
    cadence: RoutineCadence;
    scheduleJson?: RoutineSchedule;
    timeOfDay?: string | null;
    icon?: string | null;
    color?: string | null;
  }) => api.post<{ routine: Routine }>(base(homeId), body),

  update: (homeId: string, routineId: string, body: Partial<{
    name: string;
    description: string | null;
    cadence: RoutineCadence;
    scheduleJson: RoutineSchedule;
    timeOfDay: string | null;
    userId: string | null;
  }>) => api.patch<{ routine: Routine }>(`${base(homeId)}/${routineId}`, body),

  remove: (homeId: string, routineId: string) =>
    api.delete<void>(`${base(homeId)}/${routineId}`),

  checkoff: (homeId: string, routineId: string, date?: string, note?: string) =>
    api.post<{ checkin: RoutineCheckin }>(
      `${base(homeId)}/${routineId}/checkoff`,
      { date, note },
    ),

  uncheck: (homeId: string, routineId: string, date?: string) => {
    const qs = date ? `?date=${date}` : '';
    return api.delete<void>(`${base(homeId)}/${routineId}/checkoff${qs}`);
  },

  listCheckins: (homeId: string, routineId: string, since?: string) => {
    const qs = since ? `?since=${since}` : '';
    return api.get<{ checkins: RoutineCheckin[] }>(`${base(homeId)}/${routineId}/checkins${qs}`);
  },
};
